import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { AdjustOrderPopover } from '@flipster/features/src/shared/position/adjust-order/popover';
import {
  getPendingOrderKind,
  getPendingOrderRoles,
  getPendingPriceDisplayType,
  isPendingPriceEditable,
} from '@flipster/features/src/shared/position/pending-order-roles';
import { usePendingTpsl } from '@flipster/features/src/shared/position/use-pending-tpsl';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useAmendOrder, useClosePosition, useOffHoursAmendGuard } from '@flipster/features/src/shared/trade-actions';
import { FormattedMessage, T } from '@flipster/i18n';
import { accountBunja } from '@flipster/r230522/original/account';
import type { PendingPosition } from '@flipster/r230522/original/order';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { countDecimalPlaces } from '@flipster/utils/num';

import { SymbolIconCell } from '@web/app/[locale]/(web)/trade/_misc/etc-section/symbol-icon-cell';

import { useSymbolMarginType } from '../../../../../../order-form/perpetual/bunja/base';
import { yourPositionsBunja } from '../../../state';
import TpSlCol from '../../common/tpsl-col';

interface Props {
  item: PendingPosition;
  index: number;
  isOneWayMode: boolean;
}

const LimitOrderRow = ({ item, index, isOneWayMode }: Props) => {
  const transCommon = useTranslations('Common.r250401');
  const transTrade = useTranslations('Trade.r251104');
  const { symbol, side, initLeverage, isReduceOnly, price, qty, orderTpTriggerPrice, orderSlTriggerPrice } = item;
  // 이 행은 Limit 탭 행 — `limit`(일반 Limit) 아니면 `limitTpsl`(Limit (TP)/(SL))이다.
  const isTpsl = getPendingOrderKind(item) === 'limitTpsl';
  const { tpslType } = getPendingOrderRoles(item);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { cancelPosition, isCancelLoading } = useClosePosition();
  const { amendOrder } = useAmendOrder();
  // off-hours amend 알림은 팝오버 열기 전 게이트로 (reduce-only/TPSL exempt).
  const guardAmendOpen = useOffHoursAmendGuard(item);
  const marginType = useSymbolMarginType(symbol);

  const { showAllSymbolAtom } = useBunja(yourPositionsBunja);
  const { v2AccountAtom } = useBunja(accountBunja);

  const account = useAtomValue(v2AccountAtom);
  const showAllSymbol = useAtomValue(showAllSymbolAtom);

  const { tpslDisplay } = usePendingTpsl(item);

  const instrument = instrumentsKeyBySymbol?.[symbol];
  if (!instrument) return null;

  const limitAmount = Num(qty).multipliedBy(price || 0);
  const filledAmount = Num(item.amount).minus(item.leavesAmount);

  return (
    <TableRow>
      <SymbolIconCell symbol={symbol} tradeType='perpetual' index={index} />
      <TableCell>
        <div className={hstack({ gap: 4 })}>
          <OrderSideBadge orderSide={side} hideText />
          {!isReduceOnly && <LeverageBadge leverage={initLeverage} />}
        </div>
      </TableCell>
      <TableCell>
        <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5' })}>
          <T id='Autogen_common.r251126.s_Limit' />
          {isTpsl && (tpslType === 'tp' ? ' (TP)' : ' (SL)')}
        </span>
      </TableCell>
      <TableCell>
        {getPendingPriceDisplayType(item) === 'bbo' ? (
          // 타입: BBO 주문 → "BBO" 라벨
          <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-4' })}>BBO</span>
        ) : isPendingPriceEditable(item) ? (
          // 편집 가능: Limit price → 편집 popover (Limit (TP)/(SL) 행은 read-only)
          <AdjustOrderPopover
            symbol={symbol}
            side={side}
            initLeverage={initLeverage}
            marginType={marginType}
            isLimit
            currency={instrument.currency}
            quoteCurrency={instrument.quoteCurrency}
            pricePrecision={instrument.pricePrecision}
            qtyPrecision={countDecimalPlaces(instrument.unitOrderQty)}
            priceLabel={`${transTrade('Limit_price')} (${instrument.quoteCurrency})`}
            priceValue={price ?? ''}
            qtyValue={qty}
            onBeforeOpen={guardAmendOpen}
            onConfirm={(newPrice, sizeValue, sizeMode) =>
              amendOrder({
                symbol,
                orderId: item.orderId,
                price: newPrice,
                sizeMode,
                sizeValue,
                leverage: initLeverage,
              })
            }
          >
            <TextButton
              color='neutral'
              size='small'
              label={<Formatted.Number value={price} precision={instrument.pricePrecision} toFixed />}
            />
          </AdjustOrderPopover>
        ) : (
          // 편집 불가: read-only 숫자
          <Formatted.Number
            value={price}
            precision={instrument.pricePrecision}
            toFixed
            css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
          />
        )}
      </TableCell>
      {/* Triggered-but-unfilled Limit TP/SL rows reuse the price slot to show
          the Limit price; size / filled / nested TP/SL aren't meaningful for
          those rows, so we show a `-` placeholder in each remaining column. */}
      <TableCell>
        {isTpsl ? (
          '-'
        ) : (
          <div className={flex({ direction: 'column', alignItems: 'flex-start' })}>
            {!Num(qty).isZero() && (
              <AdjustOrderPopover
                symbol={symbol}
                side={side}
                initLeverage={initLeverage}
                marginType={marginType}
                isLimit
                currency={instrument.currency}
                quoteCurrency={instrument.quoteCurrency}
                pricePrecision={instrument.pricePrecision}
                qtyPrecision={countDecimalPlaces(instrument.unitOrderQty)}
                priceLabel={`${transTrade('Limit_price')} (${instrument.quoteCurrency})`}
                priceValue={price ?? ''}
                qtyValue={qty}
                onBeforeOpen={guardAmendOpen}
                onConfirm={(newPrice, sizeValue, sizeMode) =>
                  amendOrder({
                    symbol,
                    orderId: item.orderId,
                    price: newPrice,
                    sizeMode,
                    sizeValue,
                    leverage: initLeverage,
                  })
                }
              >
                <TextButton
                  color='neutral'
                  size='small'
                  label={
                    <Formatted.Number
                      value={qty}
                      precision={countDecimalPlaces(instrument.unitOrderQty)}
                      toFixed
                      abs
                      suffix={instrument.currency}
                    />
                  }
                />
              </AdjustOrderPopover>
            )}
            {!Num(limitAmount).isZero() && (
              <Formatted.Number
                value={limitAmount}
                toFixed
                abs
                precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
                suffix={instrument.quoteCurrency}
                css={{
                  textStyle: 'Text/S/Monospace/Regular',
                  color: Num(qty).isZero() ? 'content.default.level-5' : 'content.default.level-2',
                }}
              />
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        {isTpsl ? (
          '-'
        ) : (
          <Formatted.Number
            value={filledAmount}
            precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
            toFixed
            abs
            suffix={instrument.quoteCurrency}
            css={{ textStyle: 'Text/S/Monospace/Strong', color: 'content.default.level-5' }}
          />
        )}
      </TableCell>
      {(tpslDisplay === 'placeholder' || isTpsl) && (
        <>
          <TableCell>-</TableCell>
          <TableCell>-</TableCell>
        </>
      )}
      {tpslDisplay === 'visible' && !isTpsl && (
        <>
          <TpSlCol
            positionType='pending'
            parentId={item.orderId}
            side={side}
            tpSlType='tp'
            symbol={symbol}
            price={orderTpTriggerPrice}
            precision={instrument.pricePrecision}
            initLeverage={initLeverage}
            fundsInvested={limitAmount.toString()}
            tradeMode={account?.tradeMode}
            showAllSymbol={showAllSymbol}
            isReduceOnly={isReduceOnly}
            marginType={marginType}
          />
          <TpSlCol
            positionType='pending'
            parentId={item.orderId}
            side={side}
            tpSlType='sl'
            symbol={symbol}
            price={orderSlTriggerPrice}
            precision={instrument.pricePrecision}
            initLeverage={initLeverage}
            fundsInvested={limitAmount.toString()}
            tradeMode={account?.tradeMode}
            showAllSymbol={showAllSymbol}
            isReduceOnly={isReduceOnly}
            marginType={marginType}
          />
        </>
      )}
      {isOneWayMode && (
        <TableCell>
          <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5' })}>
            {isReduceOnly ? (
              <FormattedMessage id='Autogen_common.r251126.s_Yes' />
            ) : (
              <FormattedMessage id='Autogen_common.r251126.s_No' />
            )}
          </span>
        </TableCell>
      )}
      <TableCell align='right'>
        <Button
          size='small'
          color='neutral'
          hierarchy='secondary'
          onClick={() => cancelPosition(item, 'perpetual')}
          label={transCommon('Cancel')}
          isLoading={isCancelLoading}
          withSpinner
        />
      </TableCell>
    </TableRow>
  );
};

export default LimitOrderRow;
